import { useState, useCallback } from "react";
import {
  analyzeScreenshotWebAnalyzePost,
  useGetAnalysisWebAnalyzeUidGet,
} from "@/__generated__/api/web/web";
import type { AnalyzeResponse } from "@/__generated__/api/wrenAPI.schemas";

// Re-export generated types for component consumers
export type {
  ExtractedMessage,
  AboutCard,
  DidYouKnowCard,
  CoachingCard,
} from "@/__generated__/api/wrenAPI.schemas";

export function useAnalyze(paramUid?: string) {
  // Upload case: call the generated function directly (not useMutation) because
  // the upload is triggered from useEffect on mount, and useMutation's observer
  // loses track of in-flight mutations during React 18 StrictMode double-mount.
  const [result, setResult] = useState<AnalyzeResponse | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Permalink case: standard react-query hook
  const query = useGetAnalysisWebAnalyzeUidGet(paramUid!, {
    query: { enabled: !!paramUid, retry: false },
  });

  const analyze = useCallback(async (file: File) => {
    setIsPending(true);
    setError(null);
    try {
      const response = await analyzeScreenshotWebAnalyzePost({ file });
      // customInstance throws on non-2xx, so response is always the success variant
      setResult(response.data as AnalyzeResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsPending(false);
    }
  }, []);

  const loadCached = useCallback(async (jsonUrl: string) => {
    setIsPending(true);
    setError(null);
    try {
      const [res] = await Promise.all([
        fetch(jsonUrl),
        new Promise((r) => setTimeout(r, 3500)),
      ]);
      if (!res.ok) throw new Error("Failed to load example");
      const data: AnalyzeResponse = await res.json();
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsPending(false);
    }
  }, []);

  // Generated hook returns wrapped response; customInstance throws on non-2xx
  const queryData = query.data?.status === 200 ? query.data.data : undefined;
  const data = paramUid ? queryData : result;

  return {
    uid: data?.uid ?? null,
    messages: data?.messages ?? [],
    platform: data?.platform ?? null,
    about: data?.about ?? null,
    didYouKnow: data?.did_you_know ?? null,
    coaching: data?.coaching ?? [],
    isAnalyzing: paramUid ? query.isLoading : isPending,
    error: paramUid
      ? (query.error ? String(query.error) : null)
      : error,
    analyze,
    loadCached,
    reset: useCallback(() => { setResult(null); setError(null); setIsPending(false); }, []),
  };
}
